import { useState } from 'react'
import { Mail, Phone } from 'lucide-react'
import { useNavigate } from 'react-router'
import AuthContainer from '../../components/AuthContainer'
import Header from '../../components/Header'
import { useRegisterMutation } from '../../store/api/apiSlice'
import { parseErrorMessage } from '../../utils/parseErrorMessage'
import type { RegisterRequest } from '../../types'
import { UserRole } from '../../types'

export default function SignUp() {
  const navigate = useNavigate()
  const [register, { isLoading, error }] = useRegisterMutation()

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    companyName: '',
    phoneNumber: '',
    countryCode: '+234',
  })
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [formError, setFormError] = useState('')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
    setFormError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')

    if (!acceptedTerms) {
      setFormError('Please accept the Terms of Service and Privacy Policy')
      return
    }

    // Split full name into first and last name
    const nameParts = formData.fullName.trim().split(' ')
    const firstName = nameParts[0] || ''
    const lastName = nameParts.slice(1).join(' ') || nameParts[0] || ''

    if (!firstName) {
      setFormError('Please provide your full name')
      return
    }

    // Prepare phone number with country code
    const phoneNumberWithCountryCode = formData.phoneNumber.startsWith('+')
      ? formData.phoneNumber
      : `${formData.countryCode}${formData.phoneNumber.replace(/^0+/, '')}`

    const registerData: RegisterRequest = {
      email: formData.email,
      firstName,
      lastName,
      phoneNumber: phoneNumberWithCountryCode,
      role: UserRole.EMPLOYER,
      ...(formData.companyName && { companyName: formData.companyName }),
    }
    // Store phone for OTP verification flow
    localStorage.setItem('pending_verification_phone', phoneNumberWithCountryCode)

    try {
      await register(registerData).unwrap()
      // Navigate to verify account page for OTP verification (auto-login after verify)
      navigate('/verify-account', { state: { phoneNumber: phoneNumberWithCountryCode } })
    } catch (err: any) {
      // Check if user needs to verify phone number (check raw error data)
      const rawMessage = err?.data?.message || err?.message || ''
      const errorMessage = parseErrorMessage(err, 'Registration failed. Please try again.')

      if (
        (typeof rawMessage === 'string' && rawMessage.toLowerCase().includes('verify your phone number')) ||
        errorMessage.toLowerCase().includes('verify your phone number')
      ) {
        localStorage.setItem('pending_verification_phone', phoneNumberWithCountryCode)
        navigate('/verify-account', { state: { phoneNumber: phoneNumberWithCountryCode } })
        return
      }

      setFormError(errorMessage)
    }
  }

  return (
    <div className='h-screen bg-[#FFFFFF] flex flex-col overflow-y-scroll snap-y snap-mandatory'>
      <Header />
      <AuthContainer>
      <div className='w-full'>
        {/* Header */}
        <div className='mb-6'>
          <h1 className='text-gray-900 text-start text-[32px] md:text-[40px] font-bold mb-2'>
            Sign Up
          </h1>
          <p className='text-gray-500 text-start text-[14px] md:text-[15px]'>
            Lets get you started!
          </p>
          <p className='text-gray-600 text-start text-[14px] md:text-[15px]'>
            Provide us few details to create your account
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className='space-y-5'>
          {/* Error Message */}
          {(formError || error) && (
            <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'>
              {formError || parseErrorMessage(error, 'An error occurred. Please try again.')}
            </div>
          )}

          {/* Full Name */}
          <div>
            <label htmlFor='fullName' className='block text-start text-gray-900 text-[13px] font-medium mb-2'>
              Full Name / Contact Person
            </label>
            <div className='relative'>
              <Mail className='absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
              <input
                type='text'
                id='fullName'
                name='fullName'
                value={formData.fullName}
                onChange={handleChange}
                placeholder='John Doe'
                className='w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg text-[14px] placeholder-gray-400 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500'
                required
              />
            </div>
          </div>

          {/* Email and Company Name */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {/* Email */}
            <div>
              <label htmlFor='email' className='block text-start text-gray-900 text-[13px] font-medium mb-2'>
                Email
              </label>
              <div className='relative'>
                <Mail className='absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
                <input
                  type='email'
                  id='email'
                  name='email'
                  value={formData.email}
                  onChange={handleChange}
                  placeholder='john.doe@example.com'
                  className='w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg text-[14px] placeholder-gray-400 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500'
                  required
                />
              </div>
            </div>

            {/* Company Name */}
            <div>
              <label htmlFor='companyName' className='block text-start text-gray-900 text-[13px] font-medium mb-2'>
                Company Name
              </label>
              <div className='relative'>
                <Mail className='absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
                <input
                  type='text'
                  id='companyName'
                  name='companyName'
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder='Your Company Ltd'
                  className='w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg text-[14px] placeholder-gray-400 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500'
                />
              </div>
            </div>
          </div>

          {/* Phone Number with Country Code */}
          <div>
            <label htmlFor='phoneNumber' className='block text-start text-gray-900 text-[13px] font-medium mb-2'>
              Phone Number
            </label>
            <div className='flex gap-2'>
              <select
                name='countryCode'
                value={formData.countryCode}
                onChange={handleChange}
                className='w-28 px-3 py-3 border border-gray-300 rounded-lg text-[14px] outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500'
              >
                <option value='+234'>+234</option>
                <option value='+1'>+1</option>
                <option value='+44'>+44</option>
                <option value='+254'>+254</option>
                <option value='+233'>+233</option>
                <option value='+27'>+27</option>
              </select>
              <div className='relative flex-1'>
                <Phone className='absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
                <input
                  type='tel'
                  id='phoneNumber'
                  name='phoneNumber'
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder='8147330326'
                  className='w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg text-[14px] placeholder-gray-400 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500'
                  required
                />
              </div>
            </div>
          </div>

          {/* Terms Checkbox */}
          <div className='flex items-start gap-2'>
            <input
              type='checkbox'
              id='terms'
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className='mt-1 w-4 h-4 rounded border-gray-300'
              required
            />
            <label htmlFor='terms' className='text-gray-600 text-[13px]'>
              Accept{' '}
              <a href='/terms' className='text-orange-500 hover:underline'>
                Terms of Service
              </a>{' '}
              and{' '}
              <a href='/privacy' className='text-orange-500 hover:underline'>
                Privacy Policy
              </a>
            </label>
          </div>

          {/* Sign Up Button */}
          <button
            type='submit'
            disabled={isLoading}
            className='w-full bg-black text-white py-3 rounded-lg text-[15px] font-medium hover:bg-gray-900 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed'
          >
            {isLoading ? 'Creating Account...' : 'Sign Up'}
          </button>

          {/* Login Link */}
          <p className='text-center text-gray-600 text-[13px]'>
            Already have an account?{' '}
            <a href='/login' className='text-orange-500 hover:underline font-medium'>
              Log In
            </a>
          </p>
        </form>
      </div>
    </AuthContainer>
    </div>
  )
}
